/**
 * ManagerCallButton (KAN-833, KAN-863, KAN-876, KAN-877)
 *
 * "Позвонить" запускает звонок через платформу: сначала звонок менеджеру,
 * после ответа соединение с клиентом. Номер остаётся текстом рядом (его можно
 * скопировать). Пока запрос идёт, кнопка выключена. Если запрос пользователю
 * закрыт (правила роли MANAGER), вместо кнопки обычная ссылка tel: на номер карточки.
 * KAN-863: в кабинете без номера для звонков кнопка выключена с подсказкой в
 * title; у крупной кнопки (size="lg", телефон) рядом серая подпись, потому что
 * title на телефоне не виден.
 * KAN-876: showPhone={false} - номер карточки уже виден рядом (панель доски):
 * без текста этого номера, у ссылки tel: подпись "Позвонить".
 * KAN-877: сервер может звонить ЛПР, а не человеку карточки. targetMode:
 *   "eager"   - карточка одного клиента: адресаты грузятся сразу, кнопка на
 *               каждого со своим номером (ManagerCallVariants);
 *   "onClick" - строки списков: адресаты грузятся по клику, при нужде выбор;
 *   не задан  - прежняя кнопка, адресата выбирает сервер.
 */
import { Phone } from 'lucide-react';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { callTargetsUrl } from '@/services/api/callTargets';
import { managerCallUrl } from '@/services/api/managerCall';
import { telHref } from '@/components/dashboard/attentionFormat';
import type { CallButtonSize } from './ManagerCallParts';
import { CALL_LABEL, EagerTargetsCall, PickTargetCall, PlainCall, type CallViewProps } from './ManagerCallVariants';

export type CallTargetMode = 'eager' | 'onClick';

interface ManagerCallButtonProps {
  clientId: string;
  phone: string;
  testId?: string;
  className?: string;
  /** KAN-834: "lg" - крупная кнопка для телефона (приложение менеджера). */
  size?: CallButtonSize;
  /** KAN-876: false - не дублировать номер карточки текстом (он показан рядом). */
  showPhone?: boolean;
  /** KAN-877: когда узнавать адресатов звонка (см. шапку). */
  targetMode?: CallTargetMode;
}

export function ManagerCallButton({
  clientId,
  phone,
  testId = 'manager-call-button',
  className = '',
  size = 'sm',
  showPhone = true,
  targetMode,
}: ManagerCallButtonProps) {
  const canCall = useCanCallApi('POST', managerCallUrl(clientId));
  // Старый backend или менеджер без правила: прежняя кнопка без адресатов.
  const canReadTargets = useCanCallApi('GET', callTargetsUrl(clientId));
  if (!canCall) {
    return (
      <a
        href={telHref(phone)}
        data-testid={testId}
        className={`inline-flex items-center gap-1 text-sm text-blue-600 hover:underline ${className}`}
      >
        <Phone className="h-3.5 w-3.5" aria-hidden="true" />
        {showPhone ? phone : CALL_LABEL}
      </a>
    );
  }
  const view: CallViewProps = { clientId, phone, testId, className, size, showPhone };
  if (targetMode === 'eager' && canReadTargets) return <EagerTargetsCall {...view} />;
  if (targetMode === 'onClick' && canReadTargets) return <PickTargetCall {...view} />;
  return <PlainCall {...view} />;
}
